if (!customElements.get('testimonial-slider')) {
  class TestimonialSlider extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector('.testimonial-slider__track');
      this.originalSlides = Array.from(this.querySelectorAll('.testimonial-slider__slide'));
      this.prevBtn = this.querySelector('.testimonial-slider__arrow--prev');
      this.nextBtn = this.querySelector('.testimonial-slider__arrow--next');
      this.dots = Array.from(this.querySelectorAll('.testimonial-slider__dot'));

      this.isLoop = this.dataset.loop !== 'false';
      this.isAutorotate = this.dataset.autorotate === 'true';
      this.autorotateSpeed = parseInt(this.dataset.autorotateSpeed) || 5000;
      this.autoplayTimer = null;
      this.cloneOffset = 0;
      this.isAdjustingScroll = false;

      this.init();
    }

    init() {
      if (!this.track || this.originalSlides.length === 0) return;

      if (this.isLoop && this.originalSlides.length > 1) {
        this.setupInfiniteClones();
      }

      this.slides = Array.from(this.querySelectorAll('.testimonial-slider__slide'));

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.scrollPrev();
        });
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.scrollNext();
        });
      }

      if (this.dots.length > 0) {
        this.dots.forEach((dot, index) => {
          dot.addEventListener('click', (e) => {
            e.preventDefault();
            this.scrollToRealSlide(index);
          });
        });
      }

      this.track.addEventListener('scroll', () => {
        this.handleScroll();
      }, { passive: true });

      window.addEventListener('resize', this.onResize.bind(this));

      // Autoplay / Autorotate logic
      if (this.isAutorotate && this.originalSlides.length > 1) {
        this.startAutoplay();
        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => this.startAutoplay());
        this.addEventListener('touchstart', () => this.stopAutoplay(), { passive: true });
      }

      requestAnimationFrame(() => this.updateState());
    }

    setupInfiniteClones() {
      // Remove any previously created clones
      this.querySelectorAll('.testimonial-slider__slide--clone').forEach(el => el.remove());

      const cloneCount = Math.min(this.originalSlides.length, 4);
      this.cloneOffset = cloneCount;

      // Append clones of first N slides to end
      for (let i = 0; i < cloneCount; i++) {
        const cloneEnd = this.originalSlides[i].cloneNode(true);
        cloneEnd.classList.add('testimonial-slider__slide--clone');
        cloneEnd.setAttribute('aria-hidden', 'true');
        this.track.appendChild(cloneEnd);
      }

      // Prepend clones of last N slides to start
      for (let i = this.originalSlides.length - 1; i >= this.originalSlides.length - cloneCount; i--) {
        const cloneStart = this.originalSlides[i].cloneNode(true);
        cloneStart.classList.add('testimonial-slider__slide--clone');
        cloneStart.setAttribute('aria-hidden', 'true');
        this.track.insertBefore(cloneStart, this.track.firstChild);
      }

      // Set initial scroll position to point to first real slide
      requestAnimationFrame(() => {
        const step = this.getStepWidth();
        this.track.style.scrollBehavior = 'auto';
        this.track.scrollLeft = this.cloneOffset * step;
        this.track.style.scrollBehavior = 'smooth';
      });
    }

    getStepWidth() {
      if (this.cachedStepWidth) return this.cachedStepWidth;
      const firstSlide = this.querySelector('.testimonial-slider__slide');
      if (firstSlide) {
        const gap = parseInt(window.getComputedStyle(this.track).gap) || 24;
        this.cachedStepWidth = firstSlide.offsetWidth + gap;
        return this.cachedStepWidth;
      }
      return this.track.clientWidth * 0.8;
    }

    scrollPrev() {
      const step = this.getStepWidth();
      this.track.scrollBy({ left: -step, behavior: 'smooth' });
    }

    scrollNext() {
      const step = this.getStepWidth();
      this.track.scrollBy({ left: step, behavior: 'smooth' });
    }

    scrollToRealSlide(index) {
      const step = this.getStepWidth();
      const targetIndex = (this.isLoop ? this.cloneOffset : 0) + index;
      this.track.scrollTo({ left: targetIndex * step, behavior: 'smooth' });
    }

    handleScroll() {
      if (!this.track || this.isAdjustingScroll) return;

      const step = this.getStepWidth();
      if (step <= 0) return;

      const scrollLeft = this.track.scrollLeft;
      const totalOriginal = this.originalSlides.length;

      if (this.isLoop && this.cloneOffset > 0) {
        const realStartIndex = this.cloneOffset;
        const realEndIndex = this.cloneOffset + totalOriginal;
        const currentFloatIndex = scrollLeft / step;

        // Scrolled past end clones -> jump seamlessly back to matching real slide
        if (currentFloatIndex >= realEndIndex - 0.05) {
          this.isAdjustingScroll = true;
          this.track.style.scrollBehavior = 'auto';
          this.track.scrollLeft = (currentFloatIndex - totalOriginal) * step;
          this.track.style.scrollBehavior = 'smooth';
          setTimeout(() => { this.isAdjustingScroll = false; }, 50);
        }
        // Scrolled past start clones -> jump seamlessly forward to matching real slide
        else if (currentFloatIndex <= 0.05) {
          this.isAdjustingScroll = true;
          this.track.style.scrollBehavior = 'auto';
          this.track.scrollLeft = (currentFloatIndex + totalOriginal) * step;
          this.track.style.scrollBehavior = 'smooth';
          setTimeout(() => { this.isAdjustingScroll = false; }, 50);
        }
      }

      this.updateState();
    }

    updateState() {
      if (!this.track) return;

      const step = this.getStepWidth();
      const scrollLeft = this.track.scrollLeft;
      const totalOriginal = this.originalSlides.length;

      // Active Dot Calculation across all slides
      if (this.dots.length > 0 && totalOriginal > 0 && step > 0) {
        let rawIndex = Math.round(scrollLeft / step);
        if (this.isLoop && this.cloneOffset > 0) {
          rawIndex = (rawIndex - this.cloneOffset) % totalOriginal;
          if (rawIndex < 0) rawIndex += totalOriginal;
        }

        const activeIndex = Math.max(0, Math.min(rawIndex, totalOriginal - 1));

        this.dots.forEach((dot, idx) => {
          dot.classList.toggle('testimonial-slider__dot--active', idx === activeIndex);
        });
      }
    }

    startAutoplay() {
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        this.scrollNext();
      }, this.autorotateSpeed);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    onResize() {
      this.cachedStepWidth = null;
      if (this.isLoop && this.cloneOffset > 0) {
        const step = this.getStepWidth();
        this.track.style.scrollBehavior = 'auto';
        this.track.scrollLeft = this.cloneOffset * step;
        this.track.style.scrollBehavior = 'smooth';
      }
      this.updateState();
    }
  }

  customElements.define('testimonial-slider', TestimonialSlider);
}
